import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthUser } from '@app/account/account-api.model';
import { FilteredTag } from '@app/profile/components/profile-skills-list/profile-skills.model';

import {
  DataColumn,
  MenuSettings,
} from '@app/shared/components/data-table/data-table';
import { AuthService } from '@app/shared/services/auth.service';
import { camelCaseKeys, pascalCaseKeys } from '@app/shared/utils/property';
import {
  getTagCompletedRatings,
  tagHasPublicRatings,
} from '@app/shared/utils/tag-helpers';
import { WindowToken } from '@app/shared/window.token';
import { TagPublicRatingModalService } from '@app/tags/components/tag-public-rating-modal/tag-public-rating-modal.service';
import { TagActionOptionsService } from '@app/tags/services/tag-action-options.service';
import { TagFlagsService } from '@app/tags/services/tag-flags.service';
import { TagRatingTrackerService } from '@app/tags/services/tag-rating-tracker.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { UserService } from '@app/user/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { orderBy } from 'lodash-es';
import { of } from 'rxjs';
import { ProfileFilter } from './../profile-filter-bar/profile-filter-bar.component';
import { Profile } from '@app/profile/profile.model';
import { isKey, Key } from '@app/shared/key';
import { FocusStackService } from '@app/shared/services/focus-stack.service';
import { NotifierService } from '@dg/shared-services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

export const INITIAL_PAGINATION_START = 0;
export const INITIAL_PAGINATION_PAGE = 1;
export const INITIAL_PAGINATION_PER_PAGE = 20;

/*
 * ************************************************************************************
 * Please consider the `grid` view (profile-skills.component.ts) when making changes here.
 * - Will any of your changes cause undesirable side effects in the grid view (think data changes: search, sort etc)?
 * - Do you need to update the grid view to keep business logic in sync?
 * ************************************************************************************
 */
@Component({
  selector: 'dgx-profile-skills-list',
  templateUrl: './profile-skills-list.component.html',
  styleUrls: ['./profile-skills-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ProfileSkillsListComponent implements OnChanges {
  @Input() public tags: FilteredTag[];
  @Input() public isOwner: boolean;
  @Input() public owner: Profile;
  @Input() public visibleColumnTypes: InternalTagRatingTypes[];
  @Input() public filter: ProfileFilter;
  @Input() public showSignalsColumn: boolean;

  // Column templates
  @ViewChild('isFocusSkillColumnTemplate', { static: true })
  public isFocusSkillColumnTemplate: TemplateRef<any>;
  @ViewChild('skillNameColumnTemplate', { static: true })
  public skillNameColumnTemplate: TemplateRef<any>;
  @ViewChild('selfRatingColumnTemplate', { static: true })
  public selfRatingColumnTemplate: TemplateRef<any>;
  @ViewChild('managerRatingColumnTemplate', { static: true })
  public managerRatingColumnTemplate: TemplateRef<any>;
  @ViewChild('peerRatingColumnTemplate', { static: true })
  public peerRatingColumnTemplate: TemplateRef<any>;
  @ViewChild('skillReviewColumnTemplate', { static: true })
  public skillReviewColumnTemplate: TemplateRef<any>;
  @ViewChild('skillCertificationColumnTemplate', { static: true })
  public skillCertificationColumnTemplate: TemplateRef<any>;
  @ViewChild('skillTargetColumnTemplate', { static: true })
  public skillTargetColumnTemplate: TemplateRef<any>;
  @ViewChild('otherRatingsColumnTemplate', { static: true })
  public otherRatingsColumnTemplate: TemplateRef<any>;
  @ViewChild('signalCountColumnTemplate', { static: true })
  public signalCountColumnTemplate: TemplateRef<any>;

  public i18n: { [key: string]: string };
  public columns: DataColumn[] = [];
  public menuSettings: MenuSettings;
  public isLoading: boolean = true;
  public hasNoResults: boolean = true;

  /** All tags that should be viewable for given filter/search params */
  public filteredTags: FilteredTag[];

  // Pagination
  /** The current visible page of tags to display in the paginated list (subset of filtered tags) */
  public paginatedTags: FilteredTag[];
  /** Current page in pagination */
  public paginationPage = INITIAL_PAGINATION_PAGE;
  /** First item in current pagination page (of `filteredTags` total) */
  public paginationStart = INITIAL_PAGINATION_START;
  /** Pagination item limit per page */
  public paginationPerPage = INITIAL_PAGINATION_PER_PAGE;

  // Sorting
  /** Sort property */
  public sortBy: string;
  /** Track whether default sort (ascending/descending) is reversed */
  public reverseSort: boolean;
  /** Track whether sort direction is descending (for column sort arrows) */
  public isDescending: boolean;
  public focusEndpointLoading = false;

  constructor(
    private activeModal: NgbActiveModal,
    private translateService: TranslateService,
    private tagsService: TagsService,
    private userService: UserService,
    private tagRatingService: TagRatingService,
    private tagPublicRatingModalService: TagPublicRatingModalService,
    private authService: AuthService,
    private tagRatingTrackerService: TagRatingTrackerService,
    private cdr: ChangeDetectorRef,
    private tagFlagsService: TagFlagsService,
    private tagActionOptionsService: TagActionOptionsService,
    @Inject(WindowToken) private windowRef: Window,
    private route: ActivatedRoute,
    private router: Router,
    private focusStackService: FocusStackService,
    private notifier: NotifierService
  ) {
    this.i18n = this.translateService.instant([
      'Core_FocusSkill',
      'Core_FindContent',
      'dgTagRating_AddToProfile',
      'dgTagRating_RemoveFromProfile',
      'dgTagRating_RecommendSkill',
      'dgTagRating_InProgress',
      'dgTagRating_AddFocusSkill',
      'dgTagRating_RemoveFocusSkill',
      'dgTagRating_EditExpertise',
      'dgTagRating_ViewRating',
      'Ext_TopicAddedSingle',
      'SkillsSvc_AddSkillError',
    ]);
  }

  public get authUser(): AuthUser {
    return this.authService?.authUser;
  }

  public ngOnChanges({
    tags,
    visibleColumnTypes,
    filter,
  }: SimpleChanges): void {
    if (tags?.currentValue !== tags?.previousValue) {
      this.tags = tags.currentValue
        // TODO: clean up `camelCaseKeys`
        .map((tag: FilteredTag) => camelCaseKeys(tag))
        // Add sortable properties
        .map(this.setSortingProperties)
        // Only show tags matching search
        .filter((tag: FilteredTag) => tag.show);
      // reset pagination
      this.changePage(1);
    }
    if (
      visibleColumnTypes &&
      visibleColumnTypes.currentValue !== visibleColumnTypes.previousValue
    ) {
      if (visibleColumnTypes.previousValue !== undefined) {
        this.tagRatingTrackerService.trackSkillsViewChange(
          'list',
          this.tags.filter((tag) => tag.show),
          {
            ...this.gatherLayoutConfigurations(),
            previousColumnConfiguration: visibleColumnTypes.previousValue,
            columnConfiguration: visibleColumnTypes.currentValue,
          }
        );
      }

      this.setUpTable();
    }
    if (
      filter?.previousValue &&
      filter?.currentValue !== filter?.previousValue
    ) {
      this.tagRatingTrackerService.trackSkillsViewChange(
        'list',
        this.tags.filter((tag) => tag.show),
        {
          ...this.gatherLayoutConfigurations(),
          previousFilter: filter.previousValue,
          filter: filter.currentValue,
        }
      );
    }
  }

  /**
   * Get the meatball menu config.
   *
   * @param item - The current item.
   */
  public getMenuConfig = (item) => {
    return of([
      this.tagActionOptionsService.getViewSkillSignalsMenuOption(
        item,
        this.route
      ),
      {
        title: this.i18n.dgTagRating_EditExpertise,
        defaultAction: (event, target: ElementRef) => {
          this.focusStackService.push(target?.nativeElement);
          this.tagRatingService.openSkillModal(event, item, false);
        },
        isHidden: () => !this.authUser || !this.isOwner,
        preventRefocus: true,
      },
      {
        title: this.i18n.dgTagRating_ViewRating,
        defaultAction: (event, target: ElementRef) => {
          this.focusStackService.push(target?.nativeElement);
          this.userService
            .getUserByKey(item.rating.userProfileKey)
            .subscribe((user) => {
              const headerText = `${item.title} - ${user?.name}`;
              this.tagPublicRatingModalService
                .openUserPublicRatingModal(
                  item,
                  headerText,
                  event,
                  user.userProfileKey
                )
                .subscribe();
            });
        },
        isHidden: () =>
          !this.authUser || this.isOwner || item.ratings.length <= 0,
        preventRefocus: true,
      },
      {
        title: this.i18n.dgTagRating_RecommendSkill,
        defaultAction: (event, target: ElementRef) => {
          this.focusStackService.push(target?.nativeElement);
          // TODO: clean up `pascalCaseKeys`
          this.tagsService.recommendTag(pascalCaseKeys(item)).subscribe();
        },
        isHidden: () =>
          !(
            this.authUser?.canRecommendRatings &&
            !this.authUser?.isSkillInventoryClient &&
            !this.authUser?.isSkillAnalyticsClient &&
            !!this.authUser.defaultOrgInfo
          ),
      },
      {
        title: this.i18n.dgTagRating_AddFocusSkill,
        defaultAction: (event, target: ElementRef) => {
          this.focusStackService.push(target?.nativeElement);
          this.tagsService.toggleUserInterestFocus(item).subscribe();
        },
        isHidden: () =>
          !(
            !item.requestingUserIsFocused &&
            !!this.authUser &&
            !this.authUser?.isSkillInventoryClient &&
            !this.authUser?.isSkillAnalyticsClient
          ),
        preventRefocus: true,
      },
      {
        title: this.i18n.dgTagRating_RemoveFocusSkill,
        defaultAction: () =>
          this.tagsService.toggleUserInterestFocus(item).subscribe(),
        isHidden: () =>
          !(
            item.requestingUserIsFocused &&
            !!this.authUser &&
            !this.authUser?.isSkillInventoryClient &&
            !this.authUser?.isSkillAnalyticsClient
          ),
      },
      this.tagActionOptionsService.getFindContentMenuOption(item),
      {
        title: this.i18n.dgTagRating_AddToProfile,
        defaultAction: () =>
          this.tagsService
            .addUserTag({
              ...item,
              isFocused: false,
              requestingUserIsFocused: false,
              userKeyIsFocused: false,
            })
            .subscribe({
              next: () => {
                this.notifier.showSuccess(this.i18n.Ext_TopicAddedSingle);
              },
              error: () => {
                this.notifier.showError(this.i18n.SkillsSvc_AddSkillError);
              },
            }),
        isHidden: () => !!item.isFollowing || !this.authUser,
      },
      {
        title: this.i18n.dgTagRating_RemoveFromProfile,
        defaultAction: () => this.handleSkillRemoval(item),
        isHidden: () => !item.isFollowing || !this.authUser,
      },
      this.tagActionOptionsService.getAddToPlanMenuOption(item),
      this.tagActionOptionsService.getCancelManagerRatingRequestMenuOption(
        item
      ),
      this.tagActionOptionsService.getCancelSkillReviewMenuOption(item),
    ]);
  };

  private handleSkillRemoval = (item) => {
    //If it's a Workday skill we need to display an additional modal
    const isWorkdaySkill =
      item.provider?.providerName?.toLowerCase() === 'workday';
    return isWorkdaySkill
      ? this.removeWorkdaySkillModal(item)
      : this.tagsService.removeUserTag(item).subscribe();
  };

  private removeWorkdaySkillModal(item) {
    this.tagsService.removeWorkdayUserTagModal(item).subscribe({
      next: () => {
        this.activeModal.close();
        this.tagsService.removeUserTag(item).subscribe();
      },
    });
  }

  public setUpTable(): void {
    this.hasNoResults = true;
    this.isLoading = true;
    this.menuSettings = {
      bodyClasses: 'guts-p-l-half',
      getMenuConfig: this.getMenuConfig,
    };
    this.getTableColumns(this.visibleColumnTypes);
  }

  public skillClickHandler(event, tag: TagsApi.Tag): void {
    if (!this.authUser) {
      return;
    }
    if (this.isOwner) {
      this.tagRatingService.openSkillModal(event, tag, false);
    } else if (tagHasPublicRatings(tag)) {
      this.userService
        .getUserByKey(tag.rating.userProfileKey)
        .subscribe((user) => {
          const headerText = `${tag.title} - ${user?.name}`;
          this.tagPublicRatingModalService.openUserPublicRatingModal(
            tag,
            headerText,
            event,
            user.userProfileKey
          );
        });
    }
  }

  public handleSkillKeydown(event: KeyboardEvent, tag: TagsApi.Tag): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      event.preventDefault();
      event.stopPropagation();

      this.skillClickHandler(event, tag);
    }
  }

  public getSkillAriaLabel(title: string) {
    if (!this.authUser) {
      return;
    } else {
      if (this.isOwner) {
        return this.translateService.instant('dgTagRating_EditExpertiseLabel', {
          skillTitle: title,
        });
      } else {
        return this.translateService.instant('dgTagRating_ViewRatingLabel', {
          skillTitle: title,
        });
      }
    }
  }

  public updateSortHandler(sortBy: string): void {
    this.reverseSort = sortBy === this.sortBy ? !this.reverseSort : false;

    const previousSortingDirection =
      this.getSortDirectionString(this.isDescending) || '';
    this.isDescending =
      sortBy === this.columns[0].sortName
        ? this.reverseSort
        : !this.reverseSort;
    const sortingDirection =
      this.getSortDirectionString(this.isDescending) || '';

    this.tagRatingTrackerService.trackSkillsViewChange(
      'list',
      this.filteredTags?.filter((tag) => tag.show),
      {
        ...this.gatherLayoutConfigurations(),
        previousSortingColumn: this.getSortKeyString(this.sortBy) || '',
        sortingColumn: this.getSortKeyString(sortBy),
        previousSortingDirection,
        sortingDirection,
      }
    );

    this.sortBy = sortBy;
    this.sortAndPaginate();
  }

  public pageChangeHandler(page: number): void {
    this.tagRatingTrackerService.trackSkillsViewChange(
      'list',
      this.filteredTags?.filter((tag) => tag.show),
      {
        ...this.gatherLayoutConfigurations(),
        previousPagination: this.paginationPage,
        pagination: page,
      }
    );

    this.changePage(page);
    this.scrollToTop();
  }

  /**
   * Handle user changes to pagination limit
   * @param perPage
   */
  public perPageChangeHandler(perPage: number): void {
    this.paginationStart = 0;
    this.paginationPage = 1;

    this.tagRatingTrackerService.trackSkillsViewChange(
      'list',
      this.filteredTags?.filter((tag) => tag.show),
      {
        ...this.gatherLayoutConfigurations(),
        selectedResultsPerPageCount: perPage,
        previousSelectedResultsPerPageCount: this.paginationPerPage,
      }
    );

    this.paginationPerPage = perPage;
    this.sortAndPaginate();
    this.scrollToTop();
  }

  /**
   * Determine if row is clickable
   * @param tag
   * @returns boolean
   */
  // NOTE: retain `fn = () => {}` format to ensure proper binding to `this` context in child components
  public isClickable = (tag: TagsApi.Tag): boolean => {
    const hasPublicRatings = tagHasPublicRatings(tag);
    return (this.isOwner || hasPublicRatings) && !!this.authUser;
  };

  public goToSignalPage(event, tag: TagsApi.Tag): void {
    // prevent event from triggering skill modal
    event.stopPropagation();
    const url = this.router
      .createUrlTree(['../signals'], {
        relativeTo: this.route,
        queryParams: { tagId: tag.tagId },
      })
      .toString();
    this.router.navigateByUrl(url);
  }

  /**
   * Toggle which tags are focused
   *
   * @param e
   */
  public toggleFocused($event, tag): void {
    if ($event) {
      // need both if using ui-sref
      $event.preventDefault();
      $event.stopPropagation();
    }

    if (this.focusEndpointLoading) {
      return;
    }

    this.focusEndpointLoading = true;
    this.tagsService.toggleUserInterestFocus(tag).subscribe(() => {
      this.cdr.detectChanges();
      this.focusEndpointLoading = false;
    });
  }

  public getFocusSkillI18n(tag: TagsApi.TagDetails): string {
    if (tag.requestingUserIsFocused) {
      return this.translateService.instant('dgTagRating_A11yRemoveFocusSkill', {
        skillName: tag.title,
      });
    } else {
      return this.translateService.instant('dgTagRating_A11yAddFocusSkill', {
        skillName: tag.title,
      });
    }
  }

  public getFocusSkillTootip(isFocused: boolean): string {
    return isFocused
      ? this.i18n.dgTagRating_RemoveFocusSkill
      : this.i18n.dgTagRating_AddFocusSkill;
  }

  public handleSignalsKeydown(event: KeyboardEvent, tag: TagsApi.Tag): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      event.preventDefault();
      event.stopPropagation();

      this.goToSignalPage(event, tag);
    }
  }

  // takes the sortBy string & returns a readable string
  private getSortKeyString(sortBy: string): string {
    return sortBy
      ?.replace('SortKey', '')
      ?.replace(/([A-Z])/g, ' $1')
      ?.toLowerCase();
  }

  private gatherLayoutConfigurations() {
    return {
      columnConfiguration: this.visibleColumnTypes,
      filter: this.filter,
      sortingColumn: this.getSortKeyString(this.sortBy),
      sortingDirection: this.getSortDirectionString(this.isDescending),
      pagination: this.paginationPage,
      selectedResultsPerPageCount: this.paginationPerPage,
    };
  }

  private getSortDirectionString(isDescending): string {
    if (isDescending === undefined) return;
    return isDescending ? 'descending' : 'ascending';
  }

  private changePage(page: number): void {
    this.paginationStart =
      page * this.paginationPerPage - this.paginationPerPage;
    this.paginationPage = page;
    this.sortAndPaginate();
  }

  private getTableColumns(visibleColumns: InternalTagRatingTypes[]): void {
    const {
      self,
      peer,
      manager,
      evaluation,
      credential,
      target,
      signals,
      total,
    } = InternalTagRatingTypes;

    this.columns = [
      {
        label: 'Core_Focus',
        template: this.isFocusSkillColumnTemplate,
        canSort: true,
        sortName: 'isFocusSkillForOwner',
        headClasses: 'l_w-min guts-p-h-1-half',
        bodyClasses: 'l_w-min guts-p-h-1-half',
        hide:
          this.authUser?.isSkillInventoryClient ||
          this.authUser?.isSkillAnalyticsClient,
      },
      {
        label: 'Core_Skill',
        template: this.skillNameColumnTemplate,
        canSort: true,
        sortName: 'title',
        headClasses: 'profile-skills-list-rating-column',
      },
      {
        label: 'dgTagRating_SelfAssessment',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'selfRatingSortKey',
        canSort: true,
        template: this.selfRatingColumnTemplate,
        hide: !visibleColumns.includes(self),
      },
      {
        label: 'dgTagRating_PeerRatings',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'peerRatingSortKey',
        canSort: true,
        template: this.peerRatingColumnTemplate,
        hide: !visibleColumns.includes(peer),
      },
      {
        label: 'dgTagRating_ManagerAssessment',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'managerRatingSortKey',
        canSort: true,
        template: this.managerRatingColumnTemplate,
        hide: !visibleColumns.includes(manager),
      },
      {
        label: 'dgTagRating_Evaluation',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'evaluationSortKey',
        canSort: true,
        template: this.skillReviewColumnTemplate,
        hide: !visibleColumns.includes(evaluation),
      },
      {
        label: 'dgTagRating_SkillCertification',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'credentialSortKey',
        canSort: true,
        template: this.skillCertificationColumnTemplate,
        hide: !visibleColumns.includes(credential),
      },
      {
        label: 'PersonalSkillView_SkillTarget',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'targetSortKey',
        canSort: true,
        template: this.skillTargetColumnTemplate,
        hide: !visibleColumns.includes(target),
      },
      {
        label: 'dgTagRating_TotalRatings',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'totalRatingSortKey',
        canSort: true,
        template: this.otherRatingsColumnTemplate,
        hide: !visibleColumns.includes(total),
      },
      {
        label: 'dgProfileOverview_SkillSignalsTitle',
        headClasses: 'profile-skills-list-rating-column center-text no-mobile',
        bodyClasses: 'center-text no-mobile',
        sortName: 'signalCountSortKey',
        canSort: true,
        template: this.signalCountColumnTemplate,
        hide: !visibleColumns.includes(signals) || !this.showSignalsColumn,
      },
    ];

    this.isLoading = false;
    this.hasNoResults = !this.filteredTags?.length;
    this.cdr.detectChanges();
  }

  private sortAndPaginate(): void {
    // Set sort order
    this.filteredTags = this.sortBy
      ? orderBy(
          this.tags,
          this.caseInsensitiveSorter,
          this.getSortDirection(this.sortBy, this.reverseSort)
        )
      : this.tags;
    // Only render the current pagination "page" of tags
    const start = this.paginationStart;
    const end = this.paginationStart + this.paginationPerPage;
    this.paginatedTags = this.filteredTags.slice(start, end);
  }

  /**
   * Make sure string sorting is case insensitive
   * @param tag Tag
   */
  // NOTE: retain `fn = () => {}` format to ensure proper binding to `this` context
  private caseInsensitiveSorter = (tag: TagsApi.Tag): any => {
    const value = tag[this.sortBy];
    return typeof value === 'string' ? value.toLowerCase() : value;
  };

  /**
   * Add properties to a `Tag` to allow sorting on derived/nested values
   * @param Tag FilteredTag
   */
  // NOTE: retain `fn = () => {}` format to ensure proper binding to `this` context
  private setSortingProperties = (tag: FilteredTag): FilteredTag => {
    const { self, peer, manager, evaluation, credential, target } =
      InternalTagRatingTypes;
    return {
      ...tag,
      selfRatingSortKey: this.getRatingSortValue(tag, self),
      peerRatingSortKey: this.getRatingSortValue(tag, peer),
      managerRatingSortKey: this.getRatingSortValue(tag, manager),
      evaluationSortKey: this.getRatingSortValue(tag, evaluation),
      credentialSortKey: this.getRatingSortValue(tag, credential),
      targetSortKey: this.getRatingSortValue(tag, target),
      totalRatingSortKey: getTagCompletedRatings(tag).length,
      signalCountSortKey: tag.signalCount,
    };
  };

  /**
   * Get a sortable value for a given `Tag` and rating type
   * @param tag
   * @param type
   */
  private getRatingSortValue(
    tag: FilteredTag,
    type: InternalTagRatingTypes
  ): number {
    const rating = tag.ratings?.find((rating) => rating.type === type);
    const mayAddRating = this.tagRatingService.mayAddTagRatingType(
      tag,
      type,
      this.isOwner
    );
    if (rating?.level) {
      return +rating.level;
    }
    if (rating && !rating.level) {
      return 0;
    }
    if (mayAddRating) {
      return -1;
    }
    return -2;
  }

  /**
   * `title` sorts ascending first, the rest descending first
   * @param sortBy string
   * @param reverse boolean
   */
  private getSortDirection(sortBy: string, reverse: boolean): 'asc' | 'desc' {
    if (sortBy === this.columns[0].sortName) {
      return reverse ? 'desc' : 'asc';
    } else {
      return reverse ? 'asc' : 'desc';
    }
  }

  private scrollToTop(): void {
    this.windowRef.scrollTo(0, 0);
  }
}
